// src/MapComponent.tsx

import React, { useEffect, useState, useRef } from 'react';
import { GoogleMap, useLoadScript, Marker, InfoWindow, Autocomplete } from '@react-google-maps/api';
import { FieldWrapper } from '@/components/Form/FieldWrapper';
import PinDropRoundedIcon from '@mui/icons-material/PinDropRounded';
import HotelRoundedIcon from '@mui/icons-material/HotelRounded';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import BathtubRoundedIcon from '@mui/icons-material/BathtubRounded';
import CropFreeIcon from '@mui/icons-material/CropFree';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import { UseFormRegisterReturn } from 'react-hook-form';
import loc from '@/assets/loc.png';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface Property {
  id: number;
  title: string;
  image_data: string[];
  price: number;
  latitude: number;
  longitude: number;
  location: string;
  country: string;
  property_demension: string;
  bedrooms: number;
  bathrooms: number;
  created_at: string;
  subscription_type: number;
  is_featured: boolean;
  customerId__profileImage: string;
  filter_price: number;
  type: string;
  filter_date: string;
  status: string;
  is_favourite: boolean;
}

const style: React.CSSProperties | undefined = {
  right: '10px',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  color: '#aaa',
  fontSize: '18px',
  cursor: 'pointer',
  position: 'absolute',
};

const mapCss: React.CSSProperties | undefined = {
  position: 'absolute',
  left: '10px' /* Adjust as needed */,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none' /* Ensures the icon doesn't block the input field */,
};

interface Coordinates {
  lat: number;
  lng: number;
}

interface MapComponentProps {
  coordinates: Coordinates;
}

type AddressParam = {
  address: string;
  location: number[];
  lat: number;
  long: number;
  zipcode?: string;
  street_name?: string;
  house_number?: string;
  region?: string;
  city?: string;
  country?: string;
};
type PropsType = {
  label: string;
  handdleAddressChange?: (details: AddressParam) => void;
  error: any;
  registration?: Partial<UseFormRegisterReturn>;
  placeholder?: string;
  setValue?: any;
  value?: any;
};

export const MapMarker: React.FC<MapComponentProps> = ({ coordinates }) => {
  const mapStyles = {
    height: '400px',
    width: '100%',
  };
  const defaultCenter = {
    lat: coordinates.lat,
    lng: coordinates.lng,
  };
  return (
    <GoogleMap mapContainerStyle={mapStyles} zoom={10} center={defaultCenter}>
      <Marker position={defaultCenter} />
    </GoogleMap>
  );
};

export const MapMarkers: React.FC<{ coordinates: Property[] }> = ({ coordinates }) => {
  const mapStyles = {
    // height: '400px',
    height: '100%',
    maxHeight: '100%',
    width: '100%',
  };

  const defaultCenter =
    Array.isArray(coordinates) && coordinates.length > 0
      ? { lat: coordinates[0].latitude, lng: coordinates[0].longitude }
      : { lat: 0, lng: 0 };

  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const navigate = useNavigate();

  const navigateToDetailPage = (type: string, id: number) => {
    const routes: Record<string, string> = {
      BusinessSale: `/sale-detail/${id}?type=BusinessSale`,
      SaleProperty: `/property-for-sale-detail/${id}?type=SaleProperty`,
      RentProperty: `/property-for-rent-detail/${id}?type=RentProperty`,
      HouseRent: `/housing-detail/${id}?type=HouseRent`,
      InvestmentProject: `/investment-detail/${id}?type=InvestmentProject`,
      InvestmentCompany: `/company-detail/${id}?type=InvestmentCompany`,
      PropertyProject: `/property-detail/${id}?type=PropertyProject`,
      CompanySeekingEquity: `/company-seeking-equity/${id}?type=CompanySeeking`,
      PropertyDevelopers: `/house-investment-details/${id}?type=PropertyDevelopers`,
    };

    return routes[type] || `/`;
  };

  const handleMarkerClick = (property: Property) => {
    setSelectedProperty(property); // open info box first
  };

  const handleInfoWindowNavigate = (property: Property) => {
    const url = navigateToDetailPage(property.type, property.id);
    navigate(url);
  };

  return (
    <GoogleMap mapContainerStyle={mapStyles} zoom={10} center={defaultCenter}>
      {coordinates?.map((property) => (
        <Marker
          key={property.id}
          position={{ lat: property.latitude, lng: property.longitude }}
          onClick={() => handleMarkerClick(property)}
        />
      ))}

      {selectedProperty && (
        <InfoWindow
          position={{ lat: selectedProperty?.latitude, lng: selectedProperty?.longitude }}
          onCloseClick={() => setSelectedProperty(null)} // Close the InfoWindow
        >
          {(() => {
            const rawPrice =
              (selectedProperty as any)?.price ?? (selectedProperty as any)?.filter_price ?? null;
            const priceNumber = typeof rawPrice === 'number' ? rawPrice : Number(rawPrice);
            const formattedPrice = Number.isFinite(priceNumber)
              ? new Intl.NumberFormat().format(priceNumber)
              : 'N/A';
            const previewImage =
              (selectedProperty as any)?.image_data?.[0] ??
              (selectedProperty as any)?.image?.[0] ??
              (selectedProperty as any)?.property_image?.[0] ??
              '';

            return (
          <div
            role="button"
            tabIndex={0}
            onClick={() => handleInfoWindowNavigate(selectedProperty)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleInfoWindowNavigate(selectedProperty);
            }}
            style={{ cursor: 'pointer' }}
            aria-label="Open property details"
            title="Open details"
          >
            <h3 className="map-title">{selectedProperty?.title}</h3>
            <div className="map-pro-img">
              {previewImage ? <img src={previewImage} alt="property" /> : null}
            </div>
            <div className="d-flex py-3 align-items-center justify-content-between map-modal-head map-wrapper-list">
              <div className="d-flex align-items-center">
                <CurrencyRupeeIcon className="map-icon" />
                <p className="px-2 map-label">{formattedPrice}</p>
              </div>

              <div className="d-flex gap-2 align-items-center">
                <p className="px-2 d-none map-status">{selectedProperty?.status}</p>

                <p className="px-2 map-type">
                  {selectedProperty?.type?.replace(/([a-z])([A-Z])/g, '$1 $2')}
                </p>
              </div>
            </div>
            <ul className="d-flex p-0 justify-content-start align-items-center map-wrapper-list">
              <li className="d-flex align-items-center px-2">
                <HotelRoundedIcon className="map-icon" />{' '}
                <p className="px-2 map-label">{selectedProperty?.bedrooms}</p>
              </li>
              <li className="d-flex align-items-center px-2">
                <BathtubRoundedIcon className="map-icon" />{' '}
                <p className="px-2 map-label">{selectedProperty?.bedrooms}</p>
              </li>
              <li className="d-flex align-items-center px-2">
                <CropFreeIcon className="map-icon" />{' '}
                <p className="px-2 map-label">{selectedProperty?.property_demension} sq. ft.</p>
              </li>
              <li className="d-flex align-items-center px-2">
                <LoyaltyIcon className="map-icon" />{' '}
                <p className="px-2 map-label">{selectedProperty?.subscription_type}</p>
              </li>
            </ul>
            <div className="d-flex align-items-center map-wrapper-list">
              <PinDropRoundedIcon className="map-icon" />
              <p className="px-2 map-label">{selectedProperty?.location}</p>
            </div>
            <div className="d-none mt-3 align-items-center justify-content-between map-wrapper-list">
              <div>
                <p className="px-2 map-status">{selectedProperty?.status}</p>
              </div>
              <div>
                <p className="px-2 map-type">{selectedProperty?.type}</p>
              </div>
            </div>
            <div className="d-none justify-content-between align-items-center">
              <div className="left-side">
                <p>
                  <strong>Price:</strong> ₹{formattedPrice}
                </p>
                <p>
                  <strong>Location:</strong> {selectedProperty?.location}
                </p>
                <p>
                  <strong>Dimensions:</strong> {selectedProperty?.property_demension} sq. ft.
                </p>
                <p>
                  <strong>Bedrooms:</strong> {selectedProperty?.bedrooms}
                </p>
              </div>
              <div className="right-side">
                <p>
                  <strong>Bathrooms:</strong> {selectedProperty?.bathrooms}
                </p>
                <p>
                  <strong>Status:</strong> {selectedProperty?.status}
                </p>
                <p>
                  <strong>Type:</strong> {selectedProperty?.type}
                </p>
                <p>
                  <strong>Subscription Type:</strong> {selectedProperty?.subscription_type}
                </p>
              </div>
            </div>
          </div>
            );
          })()}
        </InfoWindow>
      )}
    </GoogleMap>
  );
};

export const LocationInput = (props: PropsType) => {
  const { label, handdleAddressChange, error, registration, placeholder, setValue, value } = props;
  const inputRef: any = useRef(null);
  const suggestionsRef: any = useRef(null);
  const debounceRef = useRef<any>(null);

  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const GOOGLE_PLACES_BASE_URL = 'https://places.googleapis.com/v1';
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string | undefined;

  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value);
    }
  }, [value, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setInputValue(query);
    if (typeof setValue === 'function') {
      setValue(query);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length > 2) {
      debounceRef.current = setTimeout(() => {
        getPlacePredictions(query);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const getPlacePredictions = async (query: string) => {
    if (!GOOGLE_MAPS_API_KEY) {
      // No key available — degrade gracefully
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${GOOGLE_PLACES_BASE_URL}/places:autocomplete`,
        { input: query },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
            'X-Goog-FieldMask':
              'suggestions.placePrediction.placeId,suggestions.placePrediction.text,suggestions.placePrediction.structuredFormat',
          },
        }
      );
      if (response.data && response.data.suggestions) {
        const predictions = response.data.suggestions.map((s: any) => ({
          place_id: s.placePrediction.placeId,
          description: s.placePrediction.text?.text,
          structured_formatting: {
            main_text: s.placePrediction.structuredFormat?.mainText?.text,
            secondary_text: s.placePrediction.structuredFormat?.secondaryText?.text || '',
          },
        }));
        setSuggestions(predictions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = async (suggestion: any) => {
    if (!GOOGLE_MAPS_API_KEY) {
      // Fallback — populate with description only
      const fallback = {
        address: suggestion.description,
        location: [0, 0],
        lat: 0,
        long: 0,
        zipcode: '',
        street_name: '',
        house_number: '',
        region: '',
        city: '',
        country: '',
      };
      if (typeof setValue === 'function') {
        setValue(fallback.address);
      }
      if (handdleAddressChange) handdleAddressChange(fallback);
      setInputValue(suggestion.description);
      setShowSuggestions(false);
      return;
    }

    setInputValue(suggestion.description);
    setShowSuggestions(false);
    try {
      const response = await axios.get(`${GOOGLE_PLACES_BASE_URL}/places/${suggestion.place_id}`, {
        headers: {
          'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask': 'id,displayName,formattedAddress,location,addressComponents',
        },
      });
      const place = response.data;
      const components = place?.addressComponents || [];
      let zipcode = '';
      let street_name = '';
      let house_number = '';
      let region = '';
      let city = '';
      let country = '';
      components.forEach((c: any) => {
        const types = c?.types || [];
        if (types.includes('postal_code')) zipcode = c?.longText || c?.shortText || '';
        if (types.includes('route')) house_number = c?.longText || c?.shortText || '';
        if (types.includes('street_number')) street_name = c?.longText || c?.shortText || '';
        if (types.includes('locality')) city = c?.longText || c?.shortText || '';
        if (types.includes('administrative_area_level_1'))
          region = c?.longText || c?.shortText || '';
        if (types.includes('country')) country = c?.longText || c?.shortText || '';
      });
      const lat = place?.location?.latitude || 0;
      const lng = place?.location?.longitude || 0;
      const details = {
        address: place?.formattedAddress || suggestion.description,
        location: [lat, lng],
        lat,
        long: lng,
        zipcode,
        street_name,
        house_number,
        region,
        city,
        country,
      };
      if (typeof setValue === 'function') {
        setValue(details.address);
      }
      if (handdleAddressChange) handdleAddressChange(details);
    } catch (err) {
      const fallback = {
        address: suggestion.description,
        location: [0, 0],
        lat: 0,
        long: 0,
        zipcode: '',
        street_name: '',
        house_number: '',
        region: '',
        city: '',
        country: '',
      };
      if (typeof setValue === 'function') {
        setValue(fallback.address);
      }
      if (handdleAddressChange) handdleAddressChange(fallback);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 150);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  const handleClearAddress = () => {
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (typeof setValue === 'function') {
      setValue('');
    }
    if (handdleAddressChange)
      handdleAddressChange({
        address: '',
        location: [0, 0],
        lat: 0,
        long: 0,
        zipcode: '',
        street_name: '',
        house_number: '',
        region: '',
        city: '',
        country: '',
      });
  };

  return (
    <FieldWrapper error={error} label={label}>
      <div className="position-relative">
        {placeholder ? <img src={loc} className="icon-classname" alt="map" style={mapCss} /> : null}
        <input
          ref={inputRef}
          type="text"
          className="form-control pe-5"
          style={{ paddingLeft: placeholder ? '30px' : '' }}
          placeholder={placeholder ?? 'Enter address'}
          name="streetAddress"
          {...registration}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
        {inputValue ? (
          <button
            type="button"
            onClick={handleClearAddress}
            style={style}
            aria-label="Clear address"
          >
            <i className="fa fa-xmark"></i>
          </button>
        ) : null}
        {isLoading ? (
          <div
            className="position-absolute"
            style={{ right: '36px', top: '50%', transform: 'translateY(-50%)' }}
          >
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        ) : null}
      </div>

      {showSuggestions && suggestions.length > 0 ? (
        <div
          ref={suggestionsRef}
          className="bg-white border rounded shadow position-absolute w-100 mt-1"
          style={{ zIndex: 1050, maxHeight: '240px', overflowY: 'auto' }}
        >
          {suggestions.map((s: any, idx: number) => (
            <div
              key={`${s.place_id}-${idx}`}
              className="px-3 py-2 border-bottom cursor-pointer"
              onClick={() => handleSuggestionClick(s)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleSuggestionClick(s);
                }
              }}
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
            >
              <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                {s.structured_formatting?.main_text || s.description}
              </div>
              {s.structured_formatting?.secondary_text ? (
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {s.structured_formatting?.secondary_text}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </FieldWrapper>
  );
};

export const MapViwerWithSearch = (props: PropsType) => {
  const { label, handdleAddressChange, error, registration } = props;
  const [cordinates, setCordinates] = useState<Coordinates>({
    lat: 30.71526,
    lng: 76.70777,
  });
  const autocompleteRef: any = useRef(null);
  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef?.current?.getPlace();
      const address = place.formatted_address || '';
      const lat = place?.geometry?.location?.lat();
      const lng = place?.geometry?.location?.lng();
      const details = {
        address,
        location: [lat, lng],
        lat,
        long: lng,
      };
      setCordinates({ lat, lng });
      handdleAddressChange ? handdleAddressChange(details) : null;
    }
  };
  return (
    <div className="row google-map-ip gap-2">
      <MapMarker coordinates={cordinates} />
      <FieldWrapper error={error} label={label}>
        <Autocomplete
          onLoad={(autocomplete) => {
            autocompleteRef.current = autocomplete;
          }}
          onPlaceChanged={onPlaceChanged}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Enter address"
            name="streetAddress"
            {...registration}
          />
        </Autocomplete>
      </FieldWrapper>
    </div>
  );
};

export const MapViwerWithLocationSearch = (props: PropsType) => {
  const { label, handdleAddressChange, error, registration } = props;
  const [cordinates, setCordinates] = useState<Coordinates>({
    lat: 30.71526,
    lng: 76.70777,
  });
  const autocompleteRef: any = useRef(null);
  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef?.current?.getPlace();
      const address = place.formatted_address || '';
      const lat = place?.geometry?.location?.lat();
      const lng = place?.geometry?.location?.lng();
      const details = {
        address,
        location: [lat, lng],
        lat,
        long: lng,
      };
      setCordinates({ lat, lng });
      handdleAddressChange ? handdleAddressChange(details) : null;
    }
  };
  return (
    <div className="row  google-map-ip  gap-2">
      {/* <MapMarker coordinates={cordinates} /> */}
      <FieldWrapper error={error} label={label}>
        <Autocomplete
          onLoad={(autocomplete) => {
            autocompleteRef.current = autocomplete;
          }}
          onPlaceChanged={onPlaceChanged}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Enter address"
            name="streetAddress"
            {...registration}
          />
        </Autocomplete>
      </FieldWrapper>

      <MapMarker coordinates={cordinates} />
    </div>
  );
};

const radius = 3000;
const type = 'restaurant';
// const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;
export const useNearbyPlaces = (lat: number, lng: number) => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load Google Maps API
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries: ['places'], // We need the 'places' library for PlacesService
  });

  useEffect(() => {
    if (isLoaded) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));

      service.nearbySearch(
        {
          location: new window.google.maps.LatLng(lat, lng),
          radius: radius,
          type: type,
        },
        (results: any, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setPlaces(results);
            setLoading(false);
          } else {
            console.error('Places API error: ', status);
            setLoading(false);
          }
        }
      );
    }
  }, [isLoaded, lat, lng]);

  return { places, loading };
};
